import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useMantineTableDataOperationsFromUrlParamState } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableDataOperationsFromUrlParamState';
import {
	useTranslationCapManage,
	useTranslationCommonUi,
	useTranslationMipMessages,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { DealEventLogTab } from '@dealstack/frontend/app/pages/Project/DealEventLog/DealEventLog.types.ts';
import { getDealEventLogColumns } from '@dealstack/frontend/app/pages/Project/DealEventLog/components/DealEventLogTable/DealEventLogTable.columns.tsx';
import {
	DealEventLogColumn,
	getDealEventLogQuery,
	readTextFilter,
} from '@dealstack/frontend/app/pages/Project/DealEventLog/utils/dealEventLogQuery.utils.ts';
import { useDealEventFeed } from '@dealstack/frontend/app/pages/Project/DealEventLog/useDealEventFeed.ts';
import { useDealEventActors } from '@dealstack/frontend/app/pages/Project/DealEventLog/useDealEventActors.ts';
import { useDealEventSenders } from '@dealstack/frontend/app/pages/Project/DealEventLog/useDealEventSenders.ts';
import { useDealEventSubjects } from '@dealstack/frontend/app/pages/Project/DealEventLog/useDealEventSubjects.ts';
import { useDealEventRecipients } from '@dealstack/frontend/app/pages/Project/DealEventLog/useDealEventRecipients.ts';
import { useDealEventSecurities } from '@dealstack/frontend/app/pages/Project/DealEventLog/useDealEventSecurities.ts';

export const useDealEventLogTable = (dealId: string, tab: DealEventLogTab) => {
	const tCapManage = useTranslationCapManage();
	const tMipMessages = useTranslationMipMessages();
	const tCommonUi = useTranslationCommonUi();
	const { search } = useLocation();
	const isMessaging = tab === DealEventLogTab.MESSAGING;

	// Read before the columns are built: the option lists need the values their filters already
	// hold, and this is the same state the table restores itself from.
	const mantineTableDataOperations = useMantineTableDataOperationsFromUrlParamState();
	const { columnFilters, resetAll } = mantineTableDataOperations;
	const selectedActor = readTextFilter(columnFilters, DealEventLogColumn.ACTOR);
	const selectedEntity = readTextFilter(columnFilters, DealEventLogColumn.ENTITY);
	// On messaging the actor is the sender, read from the messages themselves and searched
	// server-side; elsewhere the deal's own members are the whole population.
	const actorOptions = useDealEventActors(dealId, !isMessaging);
	const senders = useDealEventSenders(dealId, isMessaging, selectedActor);
	const recipients = useDealEventRecipients(dealId, isMessaging, selectedEntity);
	const subjects = useDealEventSubjects(
		dealId,
		tab === DealEventLogTab.TEAM_AND_ACCESS,
		selectedEntity
	);
	const securityOptions = useDealEventSecurities(dealId, tab === DealEventLogTab.CAPITAL_EVENTS);

	const columns = useMemo(
		() =>
			getDealEventLogColumns(tab, tCapManage, {
				tMessages: tMipMessages,
				tCommonUi,
				dealId,
				listSearch: search,
				actorOptions: isMessaging ? senders.options : actorOptions,
				actorAsync: isMessaging ? senders.async : undefined,
				recipientOptions: recipients.options,
				recipientAsync: recipients.async,
				subjectOptions: subjects.options,
				subjectAsync: subjects.async,
				securityOptions,
			}),
		[
			tab,
			tCapManage,
			tMipMessages,
			tCommonUi,
			dealId,
			search,
			isMessaging,
			actorOptions,
			senders.options,
			senders.async,
			recipients.options,
			recipients.async,
			subjects.options,
			subjects.async,
			securityOptions,
		]
	);

	// Filters restored from the url would otherwise apply behind a closed filter row.
	const [showColumnFilters, setShowColumnFilters] = useState(() => columnFilters.length > 0);

	const query = useMemo(() => getDealEventLogQuery(tab, columnFilters), [tab, columnFilters]);

	const feed = useDealEventFeed(dealId, query);

	return {
		columns,
		dataOperations: mantineTableDataOperations,
		showColumnFilters,
		setShowColumnFilters,
		resetFilters: resetAll,
		rows: feed.events,
		isLoading: feed.isLoading,
		isError: feed.isError,
		hasNextPage: feed.hasNextPage,
		isFetchingNextPage: feed.isFetchingNextPage,
		fetchNextPage: feed.fetchNextPage,
	};
};
